import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { workspaceAPI, rolesAPI } from '../services/api';
import { useAuth } from './AuthContext';
import type { Workspace, WorkspaceMember } from '../types';
import { mergeRolePermissions } from '../types/rbac';
import type { RolePermissions, RoleWithPermissions } from '../types/rbac';

interface WorkspaceContextType {
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  setActiveWorkspace: (ws: Workspace) => void;
  members: WorkspaceMember[];
  userRole: string[];
  isOwner: boolean;
  /**
   * 🚨 True ONLY when the members fetch succeeded AND the current user was
   * found in the returned list — i.e. `userRole` / `isOwner` are a real
   * answer rather than a placeholder.
   *
   * Without this, the three failure modes are indistinguishable from a
   * legitimately role-less member: still loading, the fetch 403'd/errored
   * (the `.catch` below sets `members: []`), or the user is absent from the
   * list. All three yield `userRole: []`, and a consumer that reads that as
   * "this user has no permissions" locks out a workspace admin. Consumers
   * MUST fall back to the global hydration payload when this is false —
   * see `hooks/usePermissions.ts`.
   */
  rolesResolved: boolean;
  /**
   * 🚨 A SEPARATE question from `rolesResolved`: has the members fetch stopped
   * being in flight (succeeded, errored, or there is no workspace to fetch)?
   *
   * Conflating the two hangs the UI. A user who is simply absent from the
   * members list — an admin-created workspace shell they were never added to —
   * leaves `rolesResolved` false FOREVER, so a consumer that spins until roles
   * resolve spins forever. Gate spinners on this; gate the role SOURCE on
   * `rolesResolved`.
   */
  membersSettled: boolean;
  /**
   * Phase 1: the member's effective per-schema verbs — the UNION of every role
   * they hold, resolved from the role catalogue.
   */
  rolePermissions: RolePermissions;
  /**
   * 🚨 True only when the catalogue actually loaded. `rolePermissions` is `{}`
   * both for "grants nothing" and for "the fetch 403'd", and those must never
   * be conflated: evaluating an empty object as authoritative denies every
   * page. Consumers fall back to legacy role-list evaluation when this is
   * false. Note plan §5's bootstrap gap — the role endpoints may themselves be
   * role-gated, so a custom-role-only member failing this fetch is a REAL
   * scenario, not a hypothetical.
   */
  permissionsReady: boolean;
  loading: boolean;
  refetch: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const WorkspaceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeWorkspace, setActiveWorkspaceState] = useState<Workspace | null>(null);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [loading, setLoading] = useState(true);

  // Lifecycle of the members fetch for the CURRENT active workspace. Reset to
  // 'loading' on every workspace switch so a stale result from the previous
  // workspace never authorizes the new one.
  const [membersStatus, setMembersStatus] =
    useState<'loading' | 'loaded' | 'error'>('loading');

  const currentMember = members.find(m => m.user_id === user?.id);
  const userRole = currentMember?.roles || [];
  const isOwner = currentMember?.is_owner || false;
  const rolesResolved = membersStatus === 'loaded' && !!currentMember;

  // Phase 1 role catalogue. Only fetched when the custom-roles flag is on —
  // with it off nothing reads permissions, so the request would be pure waste.
  const customRolesFlag = user?.features?.rbac_custom_roles === true;
  const [roleCatalogue, setRoleCatalogue] = useState<RoleWithPermissions[] | null>(null);
  const rolePermissions = React.useMemo<RolePermissions>(
    () => (roleCatalogue ? mergeRolePermissions(roleCatalogue, userRole) : {}),
    [roleCatalogue, userRole],
  );
  // Ready only when the catalogue loaded AND we know which roles to merge.
  const permissionsReady = customRolesFlag && roleCatalogue !== null && rolesResolved;
  // Settled = not in flight. Also true when there is no workspace to fetch for
  // (zero-workspace user), which would otherwise never settle.
  const membersSettled =
    membersStatus !== 'loading' || (!loading && !activeWorkspace);

  const setActiveWorkspace = useCallback((ws: Workspace) => {
    // Write localStorage BEFORE triggering React state update so the axios
    // interceptor (which reads from localStorage) sends the new
    // X-Workspace-ID header on any request fired during the subsequent
    // render pass — e.g. a page's initial-mount fetch after Layout
    // re-keys `<main>` by activeWorkspace.id.
    localStorage.setItem('active_workspace_id', ws.id);
    setActiveWorkspaceState(ws);
  }, []);

  const fetchWorkspaces = useCallback(async () => {
    if (!isAuthenticated) {
      setWorkspaces([]);
      setActiveWorkspaceState(null);
      setMembers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await workspaceAPI.list();
      const wsList: Workspace[] = res.data;
      setWorkspaces(wsList);

      const savedId = localStorage.getItem('active_workspace_id');
      const saved = wsList.find(w => w.id === savedId);
      const selected = saved || wsList[0] || null;
      setActiveWorkspaceState(selected);
      if (selected) {
        localStorage.setItem('active_workspace_id', selected.id);
      }
    } catch {
      setWorkspaces([]);
      setActiveWorkspaceState(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch members when active workspace changes
  useEffect(() => {
    setMembersStatus('loading');
    if (!activeWorkspace) {
      setMembers([]);
      return;
    }
    let cancelled = false;
    workspaceAPI.getMembers(activeWorkspace.id)
      .then(res => {
        if (cancelled) return;
        setMembers(res.data);
        setMembersStatus('loaded');
      })
      // 🚨 'error', never 'loaded': an errored fetch is NOT an empty
      // membership. Consumers fall back to the global hydration payload rather
      // than reading the empty list as "no permissions".
      .catch(() => {
        if (cancelled) return;
        setMembers([]);
        setMembersStatus('error');
      });
    return () => { cancelled = true; };
  }, [activeWorkspace]);

  // Role catalogue -> effective permissions. Re-fetched per workspace because
  // custom roles are workspace-scoped (plan §4).
  useEffect(() => {
    if (!customRolesFlag || !activeWorkspace) {
      setRoleCatalogue(null);
      return;
    }
    let cancelled = false;
    setRoleCatalogue(null);
    rolesAPI.list()
      .then(res => { if (!cancelled) setRoleCatalogue(res.data); })
      // 🚨 Stays null, never []. A failed catalogue fetch must read as
      // "unknown", so consumers fall back to legacy role evaluation instead of
      // denying every page to a member whose roles we simply could not load.
      .catch(() => { if (!cancelled) setRoleCatalogue(null); });
    return () => { cancelled = true; };
  }, [customRolesFlag, activeWorkspace]);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        activeWorkspace,
        setActiveWorkspace,
        members,
        userRole,
        isOwner,
        rolesResolved,
        membersSettled,
        rolePermissions,
        permissionsReady,
        loading,
        refetch: fetchWorkspaces,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};
